/// <amd-module name="Feed/Admin/Redux/GlobalSettings/GlobalSettingsSaga" />
import { GlobalRole } from "Feed/Common/Contracts/Feed";
import { FeedHubActions } from "Feed/Core/Redux/FeedHub/FeedHubActions";
import { SagaIterator } from "redux-saga";
import { effects } from "redux/redux-saga.compat";
import { ActionWithPayload } from "redux/Utils/ActionHelper";
import { IIdentity } from "VSS/Features/Identities/IdentityService";
import { IVssPageContext } from "VSS/Platform/Context";
import {
    IFeedGlobalSettingsService,
    IGlobalPermissionData,
    INormalizedGlobalPermission,
    IRoleAssignment
} from "../../Types";
import { GlobalSettingsActions, GlobalSettingsActionTypes } from "./GlobalSettingsActions";
import { getGlobalPermissionData } from "./GlobalSettingsSelectors";

export default function* rootSaga(): SagaIterator {
    yield effects.takeEvery(GlobalSettingsActionTypes.LoadPermissions, initializeGlobalPermissions);
    yield effects.takeEvery(GlobalSettingsActionTypes.SavePermissions, saveGlobalPermissions);
}

export function* initializeGlobalPermissions(): SagaIterator {
    try {
        yield effects.put(GlobalSettingsActions.loadStarted());
        const initialValues: IGlobalPermissionData = (yield effects.call(retrieveGlobalPermissions)) as any;
        yield effects.put(GlobalSettingsActions.loadCompleted(initialValues));
    } catch (error) {
        yield effects.put(GlobalSettingsActions.loadFailed(error.message));
    }
}

export function* saveGlobalPermissions(
    action: ActionWithPayload<GlobalSettingsActionTypes.SavePermissions, { data: IGlobalPermissionData }>
): SagaIterator {
    try {
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const settingsService = pageContext.getService<IFeedGlobalSettingsService>("IFeedGlobalSettingsService");

        const targetPermissionData = (yield effects.select(getGlobalPermissionData)) as any;
        const everyoneGroupId = (yield effects.call([settingsService, settingsService.getEveryoneGroupId])) as any;

        // since we're submitting the permission data diff, first reload the permission data
        yield effects.put(GlobalSettingsActions.saveStarted());

        const currentPermissionData: IGlobalPermissionData = (yield effects.call(retrieveGlobalPermissions)) as any;

        const modifiedRoles = getPermissionDelta(currentPermissionData, targetPermissionData, everyoneGroupId);

        yield effects.call([settingsService, settingsService.savePermissionDataAsync], modifiedRoles);
        yield effects.put(GlobalSettingsActions.saveCompleted());
        yield effects.put(FeedHubActions.hideGlobalSettings());
    } catch (error) {
        yield effects.put(GlobalSettingsActions.saveFailed(error.message));
    }
}

export function* retrieveGlobalPermissions(): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const settingsService = pageContext.getService<IFeedGlobalSettingsService>("IFeedGlobalSettingsService");

    const normalizedPermissions: INormalizedGlobalPermission[] = (yield effects.call([
        settingsService,
        settingsService.getPermissionDataAsync
    ])) as any;

    const creators = normalizedPermissions.filter(x => x.role === GlobalRole.FeedCreator);
    const admins = normalizedPermissions.filter(x => x.role === GlobalRole.Administrator);
    const customs = normalizedPermissions.filter(x => x.role === GlobalRole.Custom);

    const allIdentities = creators.concat(admins, customs);
    const identityMap: Map<string, IIdentity> = (yield effects.call(
        [settingsService, settingsService.getIIdentityFromPermissionAsync],
        allIdentities
    )) as any;

    const everyoneGroupId = settingsService.getEveryoneGroupId();
    const everyoneCreator = creators.find(x => x.identityId === everyoneGroupId);
    const everyoneIsCreator = !!everyoneCreator;

    const creatorIds = creators.reduce((result: IIdentity[], creator) => {
        const identity = identityMap.get(creator.identityId);
        if (identity != null) {
            result.push(identity);
        }
        return result;
    }, []);

    const adminIds = admins.reduce((result: IIdentity[], admin) => {
        const identity = identityMap.get(admin.identityId);
        if (identity != null) {
            result.push(identity);
        }
        return result;
    }, []);

    const customNames = customs
        .map(x => identityMap.get(x.identityId))
        .filter(x => x != null)
        .map(x => x!.displayName || x!.entityId);

    const initialValues: IGlobalPermissionData = {
        everyoneIsCreator,
        creatorIds,
        adminIds,
        customNames
    };

    return initialValues;
}

function getPermissionDelta(
    current: IGlobalPermissionData,
    target: IGlobalPermissionData,
    everyoneGroupId: string
): IRoleAssignment[] {
    const initialStateMap = new Map<string, GlobalRole>();
    const currentStateMap = new Map<string, GlobalRole>();

    const setInitialState = (list: IIdentity[], role: GlobalRole) => {
        for (const identity of list) {
            initialStateMap.set(identity.localId!, role);

            // after processing the real current state, any removed ids will be left mapped to None
            currentStateMap.set(identity.localId!, GlobalRole.None);
        }
    };

    setInitialState(current.creatorIds, GlobalRole.FeedCreator);
    setInitialState(current.adminIds, GlobalRole.Administrator);

    const setCurrentState = (list: IIdentity[], role: GlobalRole) => {
        for (const identity of list) {
            currentStateMap.set(identity.localId!, role);
        }
    };

    // do creator, then admin, so that admin takes precedence.
    setCurrentState(target.creatorIds, GlobalRole.FeedCreator);
    setCurrentState(target.adminIds, GlobalRole.Administrator);

    const modifiedRoles: IRoleAssignment[] = [];
    currentStateMap.forEach((currentRole, tfid) => {
        if (currentRole !== initialStateMap.get(tfid)) {
            modifiedRoles.push({ tfid, role: currentRole });
        }
    });

    // set new everyoneCreator last
    if (target.everyoneIsCreator !== current.everyoneIsCreator) {
        modifiedRoles.push({
            tfid: everyoneGroupId,
            role: target.everyoneIsCreator ? GlobalRole.FeedCreator : GlobalRole.None
        });
    }

    return modifiedRoles;
}
